import { useMediaQuery } from "@mantine/hooks";
import { isLargeScreenMediaQuery } from "../../../../shared/utils/ResponsiveStyles";
import {
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileTypePpt,
  IconFileTypeXls,
  IconFileTypeHtml,
  IconFile,
  Icon,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { UserDocumentType } from "@/shared/enums/UserDocumentType";
import { useGetDocument } from "@/features/sources/api/useGetDocument";
import { PdfViewer } from "../../../sources/components/PdfViewer";
import CenterLoader from "../../../../shared/components/loader/CenterLoader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import ScrollableTextWithHighlighting from "@/shared/components/scrollable-text-highlighting/ScrollableTextWithHighlighting";
import { cn } from "@/lib/utils";
import { MarkdownWithCitations } from "@/features/sources/MarkdownWithCitations";
import markdownClasses from "../../../../shared/styles/Markdown.module.css";
import { useGetDocumentPreSignedUrl } from "@/features/sources/api/useGetDocumentPreSignedUrl";

const FILE_TYPE_ICON_MAP: Record<UserDocumentType, Icon> = {
  [UserDocumentType.Pdf]: IconFileTypePdf,
  [UserDocumentType.Excel]: IconFileTypeXls,
  [UserDocumentType.Word]: IconFileTypeDoc,
  [UserDocumentType.PowerPoint]: IconFileTypePpt,
  [UserDocumentType.Text]: IconFileTypeHtml,
};

export const FileTypeIcon = ({
  type,
  isLargeScreen,
}: {
  type?: UserDocumentType;
  isLargeScreen?: boolean;
}) => {
  if (!type || !FILE_TYPE_ICON_MAP[type])
    return <IconFile className="text-primary" size={isLargeScreen ? 20 : 18} />;

  const IconComponent = FILE_TYPE_ICON_MAP[type];
  return (
    <IconComponent className="text-primary" size={isLargeScreen ? 20 : 18} />
  );
};

interface SidebarUserDocumentProps {
  documentId: string;
  chunkText?: string;
  pageNumber?: number;
  boundingBox?: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  };
  setWiderSidebar: (isWider: boolean) => void;
}

export default function SidebarUserDocument({
  documentId,
  chunkText,
  pageNumber,
  boundingBox,
  setWiderSidebar,
}: SidebarUserDocumentProps) {
  const isLargeScreen = useMediaQuery(isLargeScreenMediaQuery);
  const { document, getDocument } = useGetDocument();
  const [open, setOpen] = useState(false);
  const {
    presignedUrl,
    getPresignedUrl,
    isLoading: isLoadingUrl,
  } = useGetDocumentPreSignedUrl();

  useEffect(() => {
    getDocument(documentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  useEffect(() => {
    if (document?.documentType === UserDocumentType.Pdf) {
      getPresignedUrl(documentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, documentId]);

  useEffect(() => {
    if (document?.documentType === UserDocumentType.Pdf && presignedUrl)
      setWiderSidebar(true);
    else setWiderSidebar(false);
  }, [document, setWiderSidebar, presignedUrl]);

  if (!document) {
    return <CenterLoader />;
  }

  return document ? (
    <div className="h-full flex flex-col overflow-hidden">
      {isLoadingUrl && <CenterLoader />}
      {document.fileType !== UserDocumentType.Pdf ? (
        <>
          <div className="flex flex-col gap-2 px-3">
            <div className="flex items-center gap-1.5">
              <div
                // Uploaded document chunk highlighting is disabled as we'll need to adjust it for the refactored backend
                //onClick={() => setOpen(true)}
                className="flex items-center gap-1.5"
              >
                <FileTypeIcon
                  type={document.documentType}
                  isLargeScreen={isLargeScreen}
                />
                <span className="flex-1 text-xs font-medium text-primary whitespace-normal break-words leading-normal">
                  {document.title}
                </span>
              </div>
            </div>
            {chunkText && document.documentType !== UserDocumentType.Excel && (
              <p className="text-xs leading-normal break-words">{chunkText}</p>
            )}
            {chunkText && document.documentType === UserDocumentType.Excel && (
              <MarkdownWithCitations
                className={`${markdownClasses["markdown"]} text-xs leading-relaxed prose prose-sm max-w-none prose-p:leading-relaxed prose-pre:my-0 overflow-x-auto`}
                content={chunkText}
              />
            )}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
              className={cn(
                "max-w-[320px] sm:max-w-[646px] lg:max-w-[910px] xl:max-w-[1200px]",
                "p-0",
              )}
            >
              <DialogHeader>
                <DialogTitle className="text-sm font-bold px-4 py-2">
                  <div className="flex items-center gap-4">
                    <span>{document.title}</span>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="px-8 pb-8">
                <ScrollArea className="h-[60vh]">
                  <ScrollableTextWithHighlighting
                    fullText={""}
                    textToHighlight={chunkText}
                  />
                </ScrollArea>
              </div>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="flex-grow min-h-0 overflow-hidden">
          {document.documentType === UserDocumentType.Pdf && presignedUrl && (
            <PdfViewer
              url={presignedUrl}
              pageNumber={pageNumber ?? 1}
              boundingBox={boundingBox}
              title={document.title}
            />
          )}
        </div>
      )}
    </div>
  ) : (
    <p className="text-sm font-bold leading-relaxed break-words">
      Source document not found.
    </p>
  );
}
